/* eslint @typescript-eslint/no-explicit-any: 0 */
// ^ these rules need to be disabled because of the use of react-hook-form
import { TrashIcon, ChevronUpIcon } from '@heroicons/react/24/solid';
import { Tooltip } from '@kira/checkpoint-shared-frontend';
import React, { useEffect, useState } from 'react';
import {
  Control,
  UseFieldArrayRemove,
  useController,
  useWatch,
} from 'react-hook-form';
import { twMerge } from 'tailwind-merge';

import CTAModal from 'components/CTAModal/CTAModal';
import CounterInput from 'components/CounterInput/CounterInput';
import Dropdown from 'components/Dropdown/Dropdown';
import Panel from 'components/Panel/Panel';
import TextArea from 'components/TextArea/TextArea';
import { t } from 'locales/i18n';

interface RubricDescriptorProps {
  control: Control<any>;
  fieldName: string;
  index: number;
  remove: UseFieldArrayRemove;
  isOpen?: boolean;
}

const RubricDescriptor = ({
  control,
  fieldName,
  index,
  remove,
  isOpen,
}: RubricDescriptorProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isFocused, setIsFocused] = useState(false);

  useEffect(() => {
    setIsEditing(!!isOpen);
  }, [isOpen]);

  const value = useWatch({
    control,
    name: fieldName,
  });

  const rubricWrapperClassName = twMerge(
    'border',
    'border-kira-slate-200',
    'bg-kira-slate-50',
    'rounded-lg',
    'px-4',
    'hover:border-kira-blue-800',
    'hover:bg-kira-blue-100',
    'transition',
    'group/rubric-descriptor'
  );

  const closeButtonClassName = twMerge(
    'mr-2',
    'duration-200',
    'ease-in-out',
    'hover:bg-kira-blue-800',
    'hover:text-kira-slate-0',
    'hover:cursor-pointer',
    'inline-block',
    'rounded'
  );

  const {
    field: ratingScaleValueField,
    fieldState: { error: ratingScaleValueError },
  } = useController({
    name: `${fieldName}[${index}].rating_scale_value`,
    control,
  });
  const {
    field: textField,
    fieldState: { error: textError },
  } = useController({
    name: `${fieldName}[${index}].text`,
    control,
  });

  const {
    field: labelField,
    fieldState: { error: labelError },
  } = useController({
    name: `${fieldName}[${index}].label`,
    control,
  });

  return (
    <>
      {!isEditing ? (
        <div className={rubricWrapperClassName} data-testid="rubricCard">
          <div className="flex items-center justify-between">
            <button
              className="flex grow py-3 text-left"
              type="button"
              onClick={() => setIsEditing(true)}
            >
              <div className="flex items-center">
                <div className="ml-2">
                  <p
                    className="prose-body-md-med min-h-[24px] text-kira-slate-900"
                    data-testid="rubricName"
                  >
                    {value[index]?.label}
                  </p>
                </div>
              </div>
            </button>
            <div className="ml-4 hidden items-center group-hover/rubric-descriptor:flex">
              <Dropdown
                options={[
                  {
                    label: t('Delete'),
                    icon: <TrashIcon className="h-3.5 w-3.5" />,
                    onClick: () => setIsModalOpen(true),
                  },
                ]}
              />
            </div>
            <div
              className="ml-4 mr-2 items-center group-hover/rubric-descriptor:hidden"
              data-testid="rubricValue"
            >
              {value[index]?.rating_scale_value}
            </div>
          </div>
        </div>
      ) : (
        <Panel
          panelClassName="bg-kira-slate-50 space-y-0"
          focusedClassName="bg-white"
          isFocused={isFocused}
        >
          <div className="flex flex-col justify-self-end lg:flex-row">
            <div className="flex h-full flex-1 align-baseline">
              <TextArea
                errors={labelError}
                dataTestId="rubricTitleInput"
                errorDataTestId="rubricTitleError"
                id={`${index}-title`}
                key={`${index}-title`}
                label={t('Rubric descriptor title')}
                onChange={(e) => labelField.onChange(e.target.value)}
                onFocus={() => setIsFocused(true)}
                onBlur={() => setIsFocused(false)}
                value={value[index]?.label}
                autoFocus
              />
            </div>
            <div className="flex pb-3 align-baseline lg:ml-12">
              <CounterInput
                errors={ratingScaleValueError}
                plusClickerId="plusClicker"
                minusClickerId="minusClicker"
                idLength="scoreValueInput"
                label={t('Score value')}
                tooltipText={t(
                  'Set the numerical value for this rubric descriptor. Score values in a rubric must be consecutive.'
                )}
                setValue={(number) => ratingScaleValueField.onChange(number)}
                value={value[index]?.rating_scale_value}
                isIntegerOnly
              />
              <div className="ml-6 flex self-center">
                <button
                  className={closeButtonClassName}
                  data-testid="collapse"
                  type="button"
                  onClick={() => setIsEditing(false)}
                >
                  <Tooltip textContent={t('Collapse')} isEnabled={isEditing}>
                    <ChevronUpIcon className="m-1 h-6 w-6" />
                  </Tooltip>
                </button>
                <Dropdown
                  options={[
                    {
                      label: t('Delete'),
                      icon: <TrashIcon className="h-3.5 w-3.5" />,
                      onClick: () => setIsModalOpen(true),
                    },
                  ]}
                />
              </div>
            </div>
          </div>
          <TextArea
            errors={textError}
            id={`${index}-description`}
            key={`${index}-description`}
            label={t('Description')}
            dataTestId="descriptionInput"
            errorDataTestId="descriptionError"
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            onChange={(e) => textField.onChange(e.target.value)}
            value={value[index]?.text}
          />
        </Panel>
      )}
      <CTAModal
        isOpen={isModalOpen}
        setIsOpen={setIsModalOpen}
        title={t('Delete Rubric Descriptor')}
        description={t(
          'Deleting a rubric descriptor is permanent and can’t be undone.'
        )}
        confirmation={t('Are you sure you want to delete it?')}
        action={() => {
          remove(index);
          setIsModalOpen(false);
        }}
        buttonDataTestId="modalDelete"
        primaryButtonText={t('Delete')}
      />
    </>
  );
};

export default RubricDescriptor;
